/**
 * Plan limits and the prices shown in the product.
 *
 * Limits are enforced server-side. `priceMonthly` and `priceYearly` are
 * display amounts in USD. They were previously duplicated as literals in
 * the mobile plans API (Pro 4.99 / 49.99, Premium 9.99 / 99.99) and on the
 * subscription settings page (Pro $4.99/mo, Premium $9.99/mo).
 *
 * Stripe Checkout charges whatever price ID is set in the STRIPE_PRICE_*
 * environment variables. Those IDs do not include a dollar amount. Keep
 * these numbers in sync with the Stripe prices, and keep the limit counts
 * in sync with `mobile/lib/planLimits.ts`.
 *
 * A limit of -1 means unlimited.
 */
export type SubscriptionPlan = 'free' | 'pro' | 'premium'

type PlanCatalogEntry = {
	name: string
	tanks: number
	parameterLogsPerMonth: number
	coralAnalysesPerMonth: number
	galleryImagesPerTank: number
	priceMonthly: number
	priceYearly: number
}

export const PLAN_DETAILS = {
	free: {
		name: 'Free',
		tanks: 2,
		parameterLogsPerMonth: 30,
		coralAnalysesPerMonth: 3,
		galleryImagesPerTank: 10,
		priceMonthly: 0,
		priceYearly: 0,
	},
	pro: {
		name: 'Pro',
		tanks: 10,
		parameterLogsPerMonth: -1,
		coralAnalysesPerMonth: 25,
		galleryImagesPerTank: 100,
		priceMonthly: 4.99,
		priceYearly: 49.99,
	},
	premium: {
		name: 'Premium',
		tanks: -1,
		parameterLogsPerMonth: -1,
		coralAnalysesPerMonth: -1,
		galleryImagesPerTank: -1,
		priceMonthly: 9.99,
		priceYearly: 99.99,
	},
} as const satisfies Record<SubscriptionPlan, PlanCatalogEntry>

export type PlanDetails = (typeof PLAN_DETAILS)[SubscriptionPlan]

export const PLAN_BLURBS: Record<SubscriptionPlan, string> = {
	free: 'For a single display tank and a quarantine.',
	pro: 'For keepers running a few systems.',
	premium: 'For frag farms and big collections.',
}

export const PLAN_ORDER = [
	'free',
	'pro',
	'premium',
] as const satisfies readonly SubscriptionPlan[]
